import React, { useState, useEffect, useRef } from 'react'
import {
  Badge,
  Button,
  Table,
  TableBody,
  Typography,
  IconButton,
  Popper,
  Fade,
  Stack,
  Paper,
  ClickAwayListener
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined'
import { FormattedMessage } from 'react-intl'
import { useCookies } from 'react-cookie'
import { ICartItemUI, IReducer, ICartItem, IPrepareCartArgs, ITrialMembership, IPartialCartItem } from '../interface'
import {
  getCartFromDynamo,
  updateCart,
  getTaxAfterLogin,
  getCartFromDynamoAfterLogin,
  cleanCart,
  deleteUserCart,
  postCart
} from '../actions'
import CartItem from './CartItem'
import { removeProductFromCart, retrieveCoupon } from '../services'
import { analyticsUpdateProduct } from '../services/analytics'
import { checkInternal } from '../index'
import { notification } from '../components/NotificationEvents'
import { getAgeQuestion, getDietQuestion, isBizzaboProduct } from '../utils/bizzabo'
import TrialMembership from './TrialMembership'
import { ICalculateQuantityProps, IGetBizzaboDateProps } from '../services/analytics/analytics.types'

const Cart = () => {
  const dispatch = useDispatch()
  const cartReducer = useSelector((state: IReducer) => state.cartReducer)
  const [cookies] = useCookies(['temporary_user_id'])
  const userId = useRef('')
  const [userCountry, setUserCountry] = useState('us')
  const [trialMembershipsArray, setTrialMembershipsArray] = useState<ITrialMembership[]>([])
  const anchorRef = React.useRef<HTMLButtonElement>(null)
  const [open, setOpen] = React.useState(false)

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const [stullerAccountObj, setStullerAccountObj] = useState({})

  const handleGoCheckout = () => {
    window.dispatchEvent(new CustomEvent('changeRoute', { detail: '/checkout' }))
    handleClose()
  }
  const handleGoStore = () => {
    window.dispatchEvent(new CustomEvent('changeRoute', { detail: '/store' }))
    handleClose()
  }

  useEffect(() => {
    window.addEventListener('getUserId', setUser)
    window.addEventListener('getStullerAccount', getStullerAccount)
    window.addEventListener('getCartAfterLogin', getCartAfterLogin)
    window.addEventListener('getCartWithoutLogin', getCartWithoutLogin)
    window.addEventListener('addToCart', addToCart)
    window.addEventListener('removeProduct', removeProduct)
    window.addEventListener('applyCoupon', applyCoupon)
    window.addEventListener('applyCouponByProduct', applyCouponByProduct)
    window.addEventListener('onReadyMicrofrontendCheckout', sendCart)
    window.addEventListener('cleanCartOnDynamo', deleteCart)
    window.addEventListener('cleanCartReducer', cleanCartReducer)
    window.addEventListener('openBag', handleOpen)
    return () => {
      window.removeEventListener('getUserId', setUser)
      window.removeEventListener('getStullerAccount', getStullerAccount)
      window.removeEventListener('getCartAfterLogin', getCartAfterLogin)
      window.removeEventListener('getCartWithoutLogin', getCartWithoutLogin)
      window.removeEventListener('addToCart', addToCart)
      window.removeEventListener('removeProduct', removeProduct)
      window.removeEventListener('applyCoupon', applyCoupon)
      window.removeEventListener('applyCouponByProduct', applyCouponByProduct)
      window.removeEventListener('onReadyMicrofrontendCheckout', sendCart)
      window.removeEventListener('cleanCartOnDynamo', deleteCart)
      window.removeEventListener('cleanCartReducer', cleanCartReducer)
      window.removeEventListener('openBag', handleOpen)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartReducer, userId, stullerAccountObj])

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('onReadyMicrofrontendCart'))
  }, [])

  useEffect(() => {
    sendCart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartReducer])

  useEffect(() => {
    const trialMembershipsArray: ITrialMembership[] = Object.values(cartReducer.trialMemberships)
    setTrialMembershipsArray(trialMembershipsArray ?? [])
  }, [cartReducer.trialMemberships, userCountry])

  const sendCart = () => {
    window.dispatchEvent(new CustomEvent('sendCartToCheckout', { detail: cartReducer }))
  }

  const deleteCart = async (event: any) => {
    if (checkInternal()) {
      cleanCartReducer()
    } else {
      dispatch(deleteUserCart(event.detail))
    }
  }

  const cleanCartReducer = async () => {
    dispatch(cleanCart())
  }

  const getCartAfterLogin = async (event: any) => {
    const { coupon } = cartReducer
    const { BillToAccountObj, customerId } = event.detail
    const newCountry = BillToAccountObj.Country.toLowerCase()
    setUserCountry(newCountry)
    const newCart = cartReducer.cart
    if (checkIfUserIsRJO(BillToAccountObj.AccountNumber) && newCart.length) {
      notification.sendNotification('warning', 'notification.rjoPurchase')
      window.dispatchEvent(
        new CustomEvent('sendProductContactUs', {
          detail: {
            url: '/sendEmail',
            product: `${newCart[0].description}`
          }
        })
      )
    }
    const taxForUser = await dispatch(getTaxAfterLogin(BillToAccountObj))
    if (checkInternal()) {
      // This conditional checks if the user is rjo and if there is a bundle on cart to avoid get into the checkout
      if (checkIfUserIsRJO(BillToAccountObj.AccountNumber)) {
        if (findBundlesInCart(newCart)) {
          notification.sendNotification('error', 'notification.rjoBundle')
        }
        return
      }
      dispatch(
        postCart({
          taxes: taxForUser.data,
          coupon,
          userCart: newCart,
          countryCode: newCountry
        })
      )
    } else if (customerId) {
      if (newCart.length > 0) {
        dispatch(
          updateCart({
            customerId,
            taxes: taxForUser.data,
            coupon,
            userCart: newCart,
            stullerAccountObj
          })
        )
      } else {
        dispatch(getCartFromDynamoAfterLogin(customerId, taxForUser.data))
      }
    }
  }

  const checkIfUserIsRJO = (billTo: string) => {
    // For test RJO this must be "!=="
    return billTo.split('-')[0] === '46384'
  }

  const findBundlesInCart = (cart: ICartItemUI[]) => {
    return cart.find((i) => i.bundleItem)
  }

  const getCartWithoutLogin = () => {
    dispatch(getCartFromDynamo(cookies.temporary_user_id))
  }

  const addToCart = async (event: any) => {
    const { selectedProduct, selectedItem, _bizzaboSessionId, _bizzaboEventId, additionalSeatsQuantity } = event.detail
    try {
      const userCart = await prepareCart({
        selectedProduct,
        selectedItem,
        newBizzaboSessionId: _bizzaboSessionId,
        newBizzaboEventId: _bizzaboEventId,
        additionalSeatsQuantity
      })
      cartActions(userCart)
    } catch (error: any) {
      notification.sendNotification('error', error.message)
    }
  }

  const removeProduct = (event: any) => {
    const { cart } = cartReducer
    const userCart = removeProductFromCart(cart, event.detail)
    cartActions(userCart)
  }

  const cartActions = (userCart: any) => {
    const { taxes, coupon } = cartReducer
    checkInternal()
      ? dispatch(postCart({ taxes, coupon, userCart, countryCode: userCountry }))
      : dispatch(updateCart({ customerId: userId.current, taxes, coupon, userCart, stullerAccountObj }))
  }

  const applyCoupon = (event: any) => {
    const { taxes } = cartReducer
    const coupon = event.detail
    checkInternal()
      ? dispatch(postCart({ taxes, coupon, userCart: cartReducer.cart }))
      : dispatch(getCartFromDynamo(userId.current, taxes, coupon))
  }

  const applyCouponByProduct = (event: any) => {
    const newUserCart = cartReducer.cart.map((item: ICartItem) => ({ ...item }))
    const indexProductInCart = cartReducer.cart.findIndex(
      (item: ICartItem) => item.productId === event.detail.productId
    )
    newUserCart[indexProductInCart].coupon = event.detail.coupon
    cartActions(newUserCart)
  }

  const setUser = (event: any) => {
    userId.current = event.detail
  }

  const getStullerAccount = (event: any) => {
    if (event.detail) {
      setStullerAccountObj(event.detail)
    }
  }

  const prepareCart = async ({
    selectedProduct,
    selectedItem,
    newBizzaboSessionId,
    newBizzaboEventId,
    additionalSeatsQuantity = 0
  }: IPrepareCartArgs) => {
    let additionalSeatsQuantityDiff = 0
    const productId = selectedProduct.items[selectedItem].metadata._sku
    const boxId = selectedProduct.id
    const bizzaboDietQuestion = getDietQuestion(selectedProduct, selectedItem)
    const bizzaboAgeQuestion = getAgeQuestion(selectedProduct, selectedItem)
    const bizzaboAvailableDate = getBizzaboFormattedDate({
      selectedProduct,
      selectedItem,
      newBizzaboSessionId,
      newBizzaboEventId
    })
    const indexProductInCart = getIndexProductInCart(productId, boxId, newBizzaboSessionId)
    let userCart: IPartialCartItem[] = cartReducer.cart.map(mapToPartialCartItem)
    const isNewCartItemToBeAdded = indexProductInCart === -1
    if (isNewCartItemToBeAdded) {
      userCart = [
        ...userCart,
        {
          boxId,
          productId,
          quantity: calculateQuantity({ selectedProduct, selectedItem, additionalSeatsQuantity }),
          type: selectedProduct.items[selectedItem].type,
          bundleItem: selectedProduct.items[selectedItem].metadata._bundleItem,
          interval: selectedProduct.items[selectedItem].interval,
          bizzaboEventId: newBizzaboEventId,
          bizzaboSessionId: newBizzaboSessionId,
          bizzaboDietQuestion,
          bizzaboAgeQuestion,
          bizzaboAvailableDate,
          bizzaboAdditionalSeatsToAdd: additionalSeatsQuantity,
          coupon: await retrieveCoupon(selectedProduct.items[selectedItem].metadata._coupon, dispatch)
        }
      ]
    } else {
      userCart = [...userCart]
      if (!newBizzaboEventId) userCart[indexProductInCart].quantity++
      else {
        additionalSeatsQuantityDiff =
          additionalSeatsQuantity - (userCart[indexProductInCart].bizzaboAdditionalSeatsToAdd ?? 0)
        userCart[indexProductInCart].bizzaboAdditionalSeatsToAdd = additionalSeatsQuantity
      }
    }
    analyticsUpdateProduct({
      isNewCartItemToBeAdded,
      selectedProduct,
      selectedItem,
      mainSeatQuantity: userCart[userCart.length - 1].quantity,
      additionalSeatsQuantity,
      additionalSeatsQuantityDiff,
      newBizzaboSessionId,
      cart: cartReducer.cart
    })
    return userCart
  }

  const getBizzaboFormattedDate = ({
    selectedProduct,
    selectedItem,
    newBizzaboSessionId,
    newBizzaboEventId
  }: IGetBizzaboDateProps) => {
    return selectedProduct.items[selectedItem].bizzaboSession?.find(
      (session) => session.id.toString() === newBizzaboSessionId && session.eventId.toString() === newBizzaboEventId
    )?.availableDate
  }

  const getIndexProductInCart = (productId: string, boxId: string, bizzaboSessionId: string) => {
    return cartReducer.cart.findIndex(
      (item: ICartItem) =>
        item.productId === productId &&
        item.boxId === boxId &&
        (bizzaboSessionId ? item.bizzaboSessionId === bizzaboSessionId : true)
    )
  }

  const mapToPartialCartItem = (item: ICartItem): IPartialCartItem => ({
    boxId: item.boxId,
    productId: item.productId,
    quantity: item.quantity,
    type: item.type,
    bundleItem: item.bundleItem,
    interval: item.interval,
    bizzaboEventId: item.bizzaboEventId,
    bizzaboSessionId: item.bizzaboSessionId,
    bizzaboDietQuestion: item.bizzaboDietQuestion,
    bizzaboAgeQuestion: item.bizzaboAgeQuestion,
    bizzaboAvailableDate: item.bizzaboAvailableDate,
    bizzaboAdditionalSeatsToAdd: item.bizzaboAdditionalSeatsToAdd,
    coupon: item.coupon
  })

  const calculateQuantity = ({
    selectedProduct,
    selectedItem,
    additionalSeatsQuantity = 1
  }: ICalculateQuantityProps) => {
    if (isBizzaboProduct(selectedProduct)) {
      // For bizzabo products: if it has additional seats, the quantity of the main product is always 1
      // if not, the additional seats quantity is the quantity of the main product
      return selectedProduct.items[selectedItem].metadata._additionalSeat ? 1 : additionalSeatsQuantity
    }
    return 1
  }

  return (
    <>
      <IconButton
        ref={anchorRef}
        className={checkInternal() ? undefined : `nav-btn ${open && 'nav-selected'}`}
        color='info'
        disableTouchRipple
        onClick={handleOpen}
        data-cy='cart-button'
      >
        <Badge
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          color='primary'
          badgeContent={cartReducer.totalCartItems ?? 0}
          data-cy='cart-count'
        >
          <LocalMallOutlinedIcon sx={{ fontSize: checkInternal() ? 'inherit' : '23px' }} />
        </Badge>
      </IconButton>
      <Popper
        anchorEl={anchorRef.current}
        placement='bottom-end'
        transition
        disablePortal
        open={open}
        sx={{
          width: checkInternal() ? 500 : 'calc(100% - 1rem)',
          maxWidth: 500,
          zIndex: 999
        }}
      >
        {({ TransitionProps, placement }) => (
          <Fade {...TransitionProps} style={{ transformOrigin: placement }}>
            <Paper elevation={0} sx={{ mt: 0.25, ml: 2, borderRadius: 3, border: '1px solid #ddd' }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Stack sx={{ py: 2 }}>
                  <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {cartReducer.cart.length === 0 ? (
                      <Typography px={2} variant='body2' color='textPrimary' align='center'>
                        <FormattedMessage id='cart.yourBagIsEmpty' />
                      </Typography>
                    ) : (
                      <Table>
                        <TableBody>
                          {cartReducer.cart.map((item: ICartItemUI) => (
                            <CartItem
                              {...item}
                              key={`${item.productId}${item.bizzaboSessionId ? `-${item.bizzaboSessionId}` : ''}`}
                              userId={userId.current}
                              stullerAccountObj={stullerAccountObj}
                              internal={checkInternal()}
                              data-cy='cart-cart-item'
                            />
                          ))}
                          {trialMembershipsArray.map((item) => (
                            <TrialMembership {...item} key={item.category} />
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                  <Stack sx={{ width: '100%', mt: 2, alignItems: 'center' }}>
                    <Button
                      variant='contained'
                      onClick={handleGoCheckout}
                      sx={{ display: cartReducer.cart.length ? 'flex' : 'none' }}
                      disabled={cartReducer.cart.length === 0 || cartReducer.isCartLoading}
                      data-cy='checkout-button'
                    >
                      <FormattedMessage id='cart.proceedToCheckout' />
                    </Button>
                    <Button
                      sx={{ display: cartReducer.cart.length ? 'none' : 'flex' }}
                      variant='contained'
                      onClick={handleGoStore}
                    >
                      <FormattedMessage id='cart.goToStore' />
                    </Button>
                  </Stack>
                </Stack>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  )
}

export default Cart
